<template>
  <div class="settings container py-5">
    <header class="page-head">
      <h1 v-tx="'ClinicSettingsTitle'">{{ t('title') }}</h1>
      <p class="sub" v-tx="'ClinicSettingsSubtitle'">{{ t('subtitle') }}</p>
    </header>

    <div class="tabs">
      <button v-for="tb in shownTabs" :key="tb.key" :class="{ active: tab === tb.key }" @click="tab = tb.key" v-tx="tb.txKey">
        {{ tb.label }}
      </button>
    </div>

    <!-- PROFIL -->
    <div v-if="tab === 'profil'">
      <section class="card">
        <div class="card-head">
          <h3 v-tx="'ClinicSettingsProfileInfo'">{{ t('profileInfo') }}</h3>
          <button type="button" class="primary" :disabled="saving" @click="saveProfile" v-tx="'ClinicSettingsSave'">
            {{ saving ? t('saving') : t('save') }}
          </button>
        </div>
        <div class="profile-top">
          <span class="avatar">{{ initials }}</span>
          <div class="email-block">
            <label class="fld"><span v-tx="'ClinicSettingsEmail'">{{ t('email') }}</span>
              <input type="email" :value="profile.email" disabled />
            </label>
          </div>
        </div>
      </section>

      <section class="card">
        <h3 v-tx="'ClinicSettingsClinicData'">{{ t('clinicData') }}</h3>
        <div v-if="loadingFields" class="muted" v-tx="'ClinicSettingsLoading'">{{ t('loading') }}</div>
        <div v-else class="fields">
          <DynamicField
            v-for="f in fields"
            :key="f.id"
            :field="f"
            :value="customValues[f.key] || ''"
            @input="onFieldInput(f.key, $event)"
          />
        </div>
        <div class="card-actions">
          <button type="button" class="primary" :disabled="saving" @click="saveProfile" v-tx="'ClinicSettingsSave'">
            {{ saving ? t('saving') : t('save') }}
          </button>
        </div>
      </section>
    </div>

    <!-- SƏNƏDLƏR / DOKUMENTE -->
    <div v-else-if="tab === 'documents'">
      <section class="card">
        <h3 v-tx="'ClinicSettingsDocsAdd'">{{ t('docsAdd') }}</h3>
        <p class="muted" v-tx="'ClinicSettingsDocsDescription'">{{ t('docsDescription') }}</p>
        <form class="upload-form" @submit.prevent="uploadDoc">
          <label class="fld"><span v-tx="'ClinicSettingsDocName'">{{ t('docName') }} *</span>
            <input type="text" v-model="newDocName" required maxlength="300" :placeholder="t('docNamePlaceholder')" />
          </label>
          <label class="fld"><span v-tx="'ClinicSettingsDocFile'">{{ t('docFile') }} *</span>
            <input ref="fileInput" type="file" @change="onFileChange" required />
          </label>
          <button type="submit" class="primary" :disabled="uploading || !newDocFile || !newDocName.trim()" v-tx="'ClinicSettingsDocUpload'">
            {{ uploading ? t('docUploading') : t('docUpload') }}
          </button>
        </form>
      </section>

      <section class="card">
        <h3 v-tx="'ClinicSettingsDocsTitle'">{{ t('docsTitle') }}</h3>
        <p v-if="loadingDocs" class="muted" v-tx="'ClinicSettingsLoading'">{{ t('loading') }}</p>
        <p v-else-if="!documents.length" class="muted" v-tx="'ClinicSettingsDocsEmpty'">{{ t('docsEmpty') }}</p>
        <ul v-else class="docs-list">
          <li v-for="d in documents" :key="d.id" class="doc-row">
            <svg class="doc-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#132B59" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <div class="info">
              <strong>{{ d.name }}</strong>
              <a :href="d.fileUrl" target="_blank" rel="noopener" class="filename">{{ d.fileName }}</a>
            </div>
            <button type="button" class="link-danger" :disabled="deletingId === d.id" @click="deleteDoc(d.id)" v-tx="'ClinicSettingsDocDelete'">
              {{ deletingId === d.id ? '…' : t('docDelete') }}
            </button>
          </li>
        </ul>
      </section>
    </div>

    <!-- SICHERHEIT -->
    <div v-else-if="tab === 'sicherheit'">
      <section class="card">
        <h3 v-tx="'ClinicSettingsChangePw'">{{ t('changePw') }}</h3>
        <form class="pw-form" @submit.prevent="changePassword">
          <label class="fld"><span v-tx="'ClinicSettingsCurrentPw'">{{ t('currentPw') }}</span>
            <input type="password" v-model="pw.current" autocomplete="current-password" />
          </label>
          <label class="fld"><span v-tx="'ClinicSettingsNewPw'">{{ t('newPw') }}</span>
            <input type="password" v-model="pw.next" minlength="6" autocomplete="new-password" />
          </label>
          <label class="fld"><span v-tx="'ClinicSettingsConfirmPw'">{{ t('confirmPw') }}</span>
            <input type="password" v-model="pw.confirm" minlength="6" autocomplete="new-password" />
          </label>
          <button type="submit" class="primary" :disabled="changingPw" v-tx="'ClinicSettingsChangePw'">
            {{ changingPw ? t('saving') : t('changePw') }}
          </button>
        </form>
      </section>

      <section class="card">
        <h3 v-tx="'ClinicSettingsSession'">{{ t('session') }}</h3>
        <button type="button" class="danger" @click="logout" v-tx="'ClinicSettingsLogout'">{{ t('logout') }}</button>
      </section>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import DynamicField from "@/components/shared/DynamicField.vue";

const T = {
  az: {
    title: "Parametrlər", subtitle: "Hesab və klinika məlumatlarını idarə edin.",
    tabProfil: "Profil", tabSicherheit: "Təhlükəsizlik", tabDocuments: "Sənədlər",
    profileInfo: "Profil məlumatları", email: "E-poçt", clinicData: "Klinika məlumatları",
    save: "Yadda saxla", saving: "Yadda saxlanır…", loading: "Yüklənir…",
    changePw: "Parolu dəyiş", currentPw: "Cari parol", newPw: "Yeni parol", confirmPw: "Yeni parolu təsdiqlə",
    session: "Sessiya", logout: "Çıxış",
    saved: "Yadda saxlanıldı", pwChanged: "Parol dəyişdirildi", pwMismatch: "Parollar uyğun gəlmir",
    docsAdd: "Sənəd əlavə et",
    docsDescription: "Klinikanız haqqında əlavə məlumatlandırıcı sənədlər (broşür, video, sertifikat və s.) əlavə edin. Hər növ fayl dəstəklənir.",
    docName: "Sənədin adı", docNamePlaceholder: "Məs. Klinika broşürü", docFile: "Fayl",
    docUpload: "Yüklə", docUploading: "Yüklənir…",
    docsTitle: "Yüklənmiş sənədlər", docsEmpty: "Hələ heç bir sənəd əlavə edilməyib.", docDelete: "Sil",
    docUploaded: "Yükləndi", docDeleted: "Silindi",
  },
  de: {
    title: "Einstellungen", subtitle: "Konto- und Klinikangaben verwalten.",
    tabProfil: "Profil", tabSicherheit: "Sicherheit", tabDocuments: "Dokumente",
    profileInfo: "Profilangaben", email: "E-Mail", clinicData: "Klinikdaten",
    save: "Speichern", saving: "Wird gespeichert…", loading: "Wird geladen…",
    changePw: "Passwort ändern", currentPw: "Aktuelles Passwort", newPw: "Neues Passwort", confirmPw: "Neues Passwort bestätigen",
    session: "Sitzung", logout: "Abmelden",
    saved: "Gespeichert", pwChanged: "Passwort geändert", pwMismatch: "Passwörter stimmen nicht überein",
    docsAdd: "Dokument hinzufügen",
    docsDescription: "Fügen Sie zusätzliche informative Dokumente über Ihre Klinik hinzu (Broschüre, Video, Zertifikat usw.). Alle Dateitypen werden unterstützt.",
    docName: "Dokumentname", docNamePlaceholder: "z. B. Klinik-Broschüre", docFile: "Datei",
    docUpload: "Hochladen", docUploading: "Wird hochgeladen…",
    docsTitle: "Hochgeladene Dokumente", docsEmpty: "Noch keine Dokumente hinzugefügt.", docDelete: "Löschen",
    docUploaded: "Hochgeladen", docDeleted: "Gelöscht",
  },
  en: {
    title: "Settings", subtitle: "Manage account and clinic details.",
    tabProfil: "Profile", tabSicherheit: "Security", tabDocuments: "Documents",
    profileInfo: "Profile details", email: "Email", clinicData: "Clinic details",
    save: "Save", saving: "Saving…", loading: "Loading…",
    changePw: "Change password", currentPw: "Current password", newPw: "New password", confirmPw: "Confirm new password",
    session: "Session", logout: "Logout",
    saved: "Saved", pwChanged: "Password changed", pwMismatch: "Passwords do not match",
    docsAdd: "Add document",
    docsDescription: "Add additional informational materials about your clinic (brochure, video, certificate, etc.). All file types are supported.",
    docName: "Document name", docNamePlaceholder: "e.g. Clinic brochure", docFile: "File",
    docUpload: "Upload", docUploading: "Uploading…",
    docsTitle: "Uploaded documents", docsEmpty: "No documents added yet.", docDelete: "Delete",
    docUploaded: "Uploaded", docDeleted: "Deleted",
  },
  ru: {
    title: "Настройки", subtitle: "Управление аккаунтом и данными клиники.",
    tabProfil: "Профиль", tabSicherheit: "Безопасность", tabDocuments: "Документы",
    profileInfo: "Данные профиля", email: "Email", clinicData: "Данные клиники",
    save: "Сохранить", saving: "Сохранение…", loading: "Загрузка…",
    changePw: "Сменить пароль", currentPw: "Текущий пароль", newPw: "Новый пароль", confirmPw: "Подтвердите новый пароль",
    session: "Сессия", logout: "Выйти",
    saved: "Сохранено", pwChanged: "Пароль изменён", pwMismatch: "Пароли не совпадают",
    docsAdd: "Добавить документ",
    docsDescription: "Добавьте дополнительные информационные материалы о вашей клинике (брошюра, видео, сертификат и т.п.). Поддерживаются все типы файлов.",
    docName: "Название документа", docNamePlaceholder: "Напр. Брошюра клиники", docFile: "Файл",
    docUpload: "Загрузить", docUploading: "Загрузка…",
    docsTitle: "Загруженные документы", docsEmpty: "Документов пока нет.", docDelete: "Удалить",
    docUploaded: "Загружено", docDeleted: "Удалено",
  },
  tr: {
    title: "Ayarlar", subtitle: "Hesap ve klinik bilgilerini yönetin.",
    tabProfil: "Profil", tabSicherheit: "Güvenlik", tabDocuments: "Belgeler",
    profileInfo: "Profil bilgileri", email: "E-posta", clinicData: "Klinik bilgileri",
    save: "Kaydet", saving: "Kaydediliyor…", loading: "Yükleniyor…",
    changePw: "Şifre değiştir", currentPw: "Mevcut şifre", newPw: "Yeni şifre", confirmPw: "Yeni şifreyi onayla",
    session: "Oturum", logout: "Çıkış",
    saved: "Kaydedildi", pwChanged: "Şifre değiştirildi", pwMismatch: "Şifreler eşleşmiyor",
    docsAdd: "Belge ekle",
    docsDescription: "Kliniğiniz hakkında ek bilgilendirici materyaller ekleyin (broşür, video, sertifika vb.). Tüm dosya türleri desteklenir.",
    docName: "Belge adı", docNamePlaceholder: "Örn. Klinik broşürü", docFile: "Dosya",
    docUpload: "Yükle", docUploading: "Yükleniyor…",
    docsTitle: "Yüklenmiş belgeler", docsEmpty: "Henüz belge eklenmedi.", docDelete: "Sil",
    docUploaded: "Yüklendi", docDeleted: "Silindi",
  },
};

export default {
  name: "ClinicSettingsPage",
  components: { DynamicField },
  data() {
    return {
      tab: "profil",
      profile: { email: "", companyName: "", customValues: {} },
      fields: [],
      customValues: {},
      loadingFields: true,
      saving: false,
      pw: { current: "", next: "", confirm: "" },
      changingPw: false,
      documents: [],
      loadingDocs: true,
      newDocName: "",
      newDocFile: null,
      uploading: false,
      deletingId: null,
    };
  },
  computed: {
    lang() { return this.$store.state.lang || "az"; },
    shownTabs() {
      return [
        { key: "profil", txKey: "ClinicSettingsTabProfil", label: this.t("tabProfil") },
        { key: "documents", txKey: "ClinicSettingsTabDocuments", label: this.t("tabDocuments") },
        { key: "sicherheit", txKey: "ClinicSettingsTabSicherheit", label: this.t("tabSicherheit") },
      ];
    },
    initials() {
      const src = (this.profile.companyName || this.profile.email || "").trim();
      const parts = src.split(/\s+/).filter((p) => /^[\p{L}]/u.test(p));
      const r = ((parts[0] || "").charAt(0) + (parts[1] || "").charAt(0)).toUpperCase();
      return r || (src.slice(0, 2).toUpperCase() || "?");
    },
  },
  mounted() {
    this.loadFields().then(() => this.loadProfile());
    this.loadDocs();
  },
  methods: {
    t(key) {
      // Admin paneldən idarə olunan override (statik mətnlər) — açar: ClinicSettings + Key
      const sKey = "ClinicSettings" + key.charAt(0).toUpperCase() + key.slice(1);
      const ov = this.$store.state.statics && this.$store.state.statics[sKey];
      if (ov && String(ov).trim()) return ov;
      const d = T[this.lang] || T.az;
      return d[key] != null ? d[key] : (T.az[key] || key);
    },
    async loadFields() {
      try {
        const res = await axios.get(`/clinic-fields`);
        this.fields = (res.data?.data || [])
          .filter((f) => f.isActive && f.fieldType !== 9)
          .sort((a, b) => a.orderIndex - b.orderIndex);
      } catch { this.fields = []; } finally { this.loadingFields = false; }
    },
    async loadProfile() {
      try {
        const res = await axios.get(`/clinic/profile`);
        const p = res.data?.data || {};
        this.profile = { email: p.email || "", companyName: p.companyName || "", customValues: p.customValues || {} };
        const init = {};
        this.fields.forEach((f) => { init[f.key] = (p.customValues || {})[f.key] || ""; });
        this.customValues = init;
      } catch { /* toast */ }
    },
    onFieldInput(key, val) { this.$set(this.customValues, key, val); },
    async saveProfile() {
      this.saving = true;
      try {
        await axios.put(`/clinic/profile`, { customValues: this.customValues });
        if (this.$toast) this.$toast.success(this.t("saved"));
        await this.loadProfile();
      } catch { /* toast */ } finally { this.saving = false; }
    },
    async changePassword() {
      if (this.pw.next !== this.pw.confirm) {
        if (this.$toast) this.$toast.error(this.t("pwMismatch"));
        return;
      }
      this.changingPw = true;
      try {
        await axios.post(`/identity/changePassword`, {
          email: this.profile.email || this.$store.getters["auth/email"],
          currentPassword: this.pw.current,
          newPassword: this.pw.next,
          confirmNewPassword: this.pw.confirm,
        });
        if (this.$toast) this.$toast.success(this.t("pwChanged"));
        this.pw = { current: "", next: "", confirm: "" };
      } catch { /* toast */ } finally { this.changingPw = false; }
    },
    async loadDocs() {
      this.loadingDocs = true;
      try {
        const res = await axios.get(`/clinic/documents`);
        this.documents = res.data?.data || [];
      } catch { this.documents = []; } finally { this.loadingDocs = false; }
    },
    onFileChange(ev) {
      this.newDocFile = (ev.target.files && ev.target.files[0]) || null;
    },
    async uploadDoc() {
      if (!this.newDocFile || !this.newDocName.trim()) return;
      this.uploading = true;
      try {
        const fd = new FormData();
        fd.append("Name", this.newDocName.trim());
        fd.append("File", this.newDocFile);
        await axios.post(`/clinic/documents`, fd);
        if (this.$toast) this.$toast.success(this.t("docUploaded"));
        this.newDocName = "";
        this.newDocFile = null;
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        await this.loadDocs();
      } catch { /* toast */ } finally { this.uploading = false; }
    },
    async deleteDoc(id) {
      this.deletingId = id;
      try {
        await axios.delete(`/clinic/documents/${id}`);
        if (this.$toast) this.$toast.success(this.t("docDeleted"));
        await this.loadDocs();
      } catch { /* toast */ } finally { this.deletingId = null; }
    },
    logout() {
      this.$store.dispatch("auth/logout");
      this.$router.push({ name: "home" });
    },
  },
};
</script>

<style lang="scss" scoped>
.settings { max-width: 900px; font-family: "Roboto", "Segoe UI", Arial, sans-serif; }

.page-head { margin-bottom: 16px;
  h1 { margin: 0 0 6px; font-size: 22px; font-weight: 700; color: #132B59; line-height: 1; }
  .sub { margin: 0; font-size: 13px; color: #666F82; }
}

.tabs { display: flex; gap: 4px; background: #fff; border: 1px solid #E0E3EA; border-radius: 12px; padding: 6px; margin-bottom: 16px;
  button { flex: 0 0 auto; padding: 8px 16px; border: 0; background: transparent; border-radius: 8px; font: inherit; font-size: 13px; font-weight: 600; color: #666F82; cursor: pointer;
    &.active { background: #132B59; color: #fff; }
    &:hover:not(.active) { background: #F5F6FA; }
  }
}

.card { background: #fff; border: 1px solid #E0E3EA; border-radius: 14px; padding: 20px; margin-bottom: 16px;
  h3 { margin: 0 0 16px; font-size: 15px; font-weight: 700; color: #151924; }
  .muted { color: #9EA6B6; font-size: 13px; }
  .card-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;
    h3 { margin: 0; }
  }
  .card-actions { margin-top: 16px; display: flex; justify-content: flex-end; }
}

.profile-top { display: flex; align-items: center; gap: 18px;
  .avatar { width: 64px; height: 64px; flex-shrink: 0; border-radius: 50%; background: #132B59; color: #fff; font-size: 20px; font-weight: 700; display: flex; align-items: center; justify-content: center; text-transform: uppercase; }
  .email-block { flex: 1; min-width: 0; }
}

.fields { display: flex; flex-direction: column; gap: 4px; }

.fld { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px;
  span { font-size: 12px; font-weight: 600; color: #475569; }
  input { padding: 10px 12px; border: 1px solid #E0E3EA; border-radius: 8px; font: inherit; font-size: 14px; outline: none;
    &:focus { border-color: #132B59; }
    &:disabled { background: #F5F6FA; color: #64748b; }
  }
}

.pw-form { display: flex; flex-direction: column; max-width: 420px;
  .primary { align-self: flex-start; margin-top: 4px; }
}

.upload-form { max-width: 460px;
  .primary { margin-top: 4px; }
}

.docs-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px;
  .doc-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1px solid #E0E3EA; border-radius: 10px; background: #fff;
    .doc-icon { flex-shrink: 0; }
    .info { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;
      strong { color: #151924; font-size: 14px; }
      .filename { color: #132B59; font-size: 12.5px; text-decoration: none; word-break: break-all;
        &:hover { text-decoration: underline; }
      }
    }
    .link-danger { background: transparent; border: 0; color: #c0392b; cursor: pointer; font: inherit; font-size: 13px; font-weight: 600; padding: 4px 8px;
      &:hover:not(:disabled) { text-decoration: underline; }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
  }
}

.card .muted { color: #9EA6B6; font-size: 13px; margin: 0 0 14px; }

.primary { height: 40px; padding: 0 18px; border: 0; border-radius: 8px; background: #132B59; color: #fff; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover:not(:disabled) { background: #0e2147; } &:disabled { opacity: 0.6; cursor: not-allowed; } }
.danger { height: 40px; padding: 0 18px; border: 1px solid #f1c2bd; border-radius: 8px; background: #fff; color: #c0392b; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover { background: #fdecea; } }
</style>
